import { fetchConversation, readChat } from '@app/api'
import { fetchConversationParticipant } from '@app/api/communications/fetchConversationParticipant'
import config from '@app/config'
import {
  getConversationById,
  getMessages,
  initialConversationItem,
} from '@app/helpers'
import {
  ExistThreadEventCapturedPayload,
  UnknownThreadEventCapturedPayload,
} from '@app/hooks'
import { MessageItem } from '@app/models'
import { handleForbiddenConversationError } from '@app/utils'
import { isImpersonatedB2BSession } from '@app/utils/token/isImpersonatedB2BSession'

import { logger } from '@lib/utils'

import { fetchMessageDetailsSafely } from './fetchMessageDetailsSafely'
import { handleSuggestedActionCreated } from './handleSuggestedActionCreated'
import type { ThreadMessageReceivedEvent } from '../../types'
import type { CreateOnChatMessageReceivedParams } from '../types'

export const createOnChatMessageReceived = ({
  refState,
  refCurrentConversationId,
  refReceivedMessages,
  currentUserId,
  userLanguage,
  dispatch,
  getDependencies,
  getReceivedUnreadStatus,
  forceUpdateConversation,
  readConversation,
  onConversationForbidden,
}: CreateOnChatMessageReceivedParams) => {
  return async (event: ThreadMessageReceivedEvent) => {
    logger('onChatMessageReceived', event, '#c203fc')

    if (event.metadata.EventType === 'ConversationUpdated') {
      await forceUpdateConversation(event.threadId)
      return
    }

    if (event.metadata.EventType === 'ConversationRead') {
      await readConversation(event.threadId, event.metadata.FromParticipantId)
      return
    }

    const conversation = getConversationById(
      refState.current.conversations,
      event.threadId
    )

    if (event.metadata.EventType === 'SuggestedActionCreated') {
      await handleSuggestedActionCreated({
        event,
        conversation,
        dispatch,
        getDependencies,
        getReceivedUnreadStatus,
        onConversationForbidden,
      })

      return
    }

    const isMessageExist = refReceivedMessages.current.has(
      event.metadata.InternalMessageId
    )

    if (isMessageExist) {
      return
    }

    refReceivedMessages.current.add(event.metadata.InternalMessageId)

    if (conversation) {
      const receivedMessageIndex = conversation.messages?.findIndex(
        (item) =>
          item.uuid ===
          (event.metadata.ArtificialId ?? event.metadata.InternalMessageId)
      )

      const isReceivedMessageExist = receivedMessageIndex !== -1
      if (isReceivedMessageExist) {
        if (
          refState.current.acsChatParticipantId ===
          event.sender.communicationUserId
        ) {
          conversation.messages[receivedMessageIndex] = {
            ...conversation.messages[receivedMessageIndex],
            creationDate: event.metadata.CreationDate,
            updateDate:
              event.metadata.UpdateDate || event.metadata.CreationDate,
          }

          dispatch({
            type: 'EXIST_MESSAGE_RECEIVED',
            payload: {
              conversationId: conversation.id,
              uuid: event.metadata.ArtificialId,
              creationDate: event.metadata.CreationDate,
              updateDate:
                event.metadata.UpdateDate || event.metadata.CreationDate,
            },
          })
        }

        return
      }

      const isSenderInParticipants = conversation?.participants?.some(
        (item) => item.id.toString() === event.metadata.FromParticipantId
      )

      const isMessageFromCurrentUser = conversation?.participants?.some(
        (item) =>
          item.id.toString() === event.metadata.FromParticipantId &&
          item.userId === currentUserId
      )

      if (!isSenderInParticipants && event.metadata.Source !== 'System') {
        const participant = (
          await fetchConversationParticipant(
            conversation.id,
            parseInt(event.metadata.FromParticipantId, 10)
          )
        )?.participant

        if (participant) {
          conversation.participants = [
            ...conversation.participants,
            participant,
          ]
        }
      }

      if (isSenderInParticipants || event.metadata.Source === 'System') {
        const conversationLanguage =
          conversation.messages &&
          conversation.messages.find(
            (x) =>
              x.content?.language !== undefined &&
              x.content?.language != userLanguage
          )?.content?.language

        const languageExceptions =
          config.regionalSettings?.languageExceptions || []
        const isTranslationEnabled = !!(
          conversationLanguage &&
          !languageExceptions.includes(conversationLanguage)
        )

        const {
          translations,
          content,
          attachments: messageAttachments,
          systemNotificationType,
          recipientParticipantId,
          buyerOrRenterParticipantId,
          callerParticipantId,
          toParticipantId,
          fromParticipantId,
        } = await fetchMessageDetailsSafely(
          conversation.id,
          parseInt(event.metadata.InternalMessageId, 10),
          event.message,
          event.metadata.Source,
          isTranslationEnabled
        )

        const normalizedFromParticipantId =
          fromParticipantId ?? parseInt(event.metadata.FromParticipantId, 10)

        const message: MessageItem = {
          translations,
          attachments: messageAttachments,
          content,
          acsChatMessageId: event.threadId,
          creationDate: event.metadata.CreationDate,
          id: parseInt(event.metadata.InternalMessageId, 10),
          source: event.metadata.Source,
          type: 'Text',
          updateDate: event.metadata.UpdateDate || event.metadata.CreationDate,
          state: event.metadata.State || 'Pending',
          systemNotificationType,
          recipientParticipantId,
          buyerOrRenterParticipantId,
          callerParticipantId,
          toParticipantId,
          fromParticipantId: normalizedFromParticipantId,
        }

        let unreadStatus = false
        if (!isMessageFromCurrentUser) {
          unreadStatus = getReceivedUnreadStatus(
            conversation.id,
            conversation.participants
          )
        }

        const payload: ExistThreadEventCapturedPayload = {
          conversation: {
            ...conversation,
            messages: [message, ...conversation.messages],
            updateDate: message.updateDate,
            unread: unreadStatus,
          },
        }

        dispatch({
          type: 'EXIST_THREAD_EVENT_CAPTURED',
          payload,
        })
      }
    } else {
      let conversation: Awaited<ReturnType<typeof fetchConversation>>
      try {
        conversation = await fetchConversation(event.threadId)
      } catch (error) {
        if (handleForbiddenConversationError(error, onConversationForbidden)) {
          return
        }

        throw error
      }

      const { listings, accounts } = await getDependencies([conversation])

      const messages = getMessages(conversation.lastMessage)

      const payload: UnknownThreadEventCapturedPayload = {
        listings,
        accounts,
        conversation: {
          ...initialConversationItem,
          ...conversation,
          messages,
          total: 0,
          unread: getReceivedUnreadStatus(
            conversation.id,
            conversation.participants
          ),
        },
      }

      dispatch({
        type: 'UNKNOWN_THREAD_EVENT_CAPTURED',
        payload,
      })
    }

    if (
      refCurrentConversationId.current?.toString() ===
        event.metadata.ConversationId &&
      !isImpersonatedB2BSession()
    ) {
      const conversationId = refCurrentConversationId.current

      if (conversationId !== null) {
        await readChat(
          refState.current.acsChatParticipantId,
          refState.current.acsChatToken.token,
          conversationId
        )
      }
    }
  }
}
